import { useAutresClients } from '../../api/useConversations';
import EtatErreur from '../../components/EtatErreur';
import Text from '../../components/Text';
import { echecSansDonnee } from '../../helpers/requete';
import { initiales, nomComplet } from '../../helpers/conversation';
import { t } from '../../i18n/langue';

interface AutresClientsProps {
    facilityId: number | null;
    terme: string;
    /** Les clients déjà affichés dans les conversations : pas deux fois la même ligne. */
    dejaAffiches: Set<number>;
    estGroupe: boolean;
    onOuvrir: (customerId: number) => void;
}

/**
 * La seconde section de la recherche : les clients du cardex sans conversation
 * dans la liste — évo Odomo du 09/10/2026.
 *
 * La recherche des conversations ne retrouve que les clients qui ont déjà écrit
 * ou reçu un message manuel. À l'accueil, on cherche aussi celui qui n'a jamais
 * écrit, pour lui envoyer un premier message : sans cette section, il fallait
 * passer par le site. Ouvrir la fiche suffit : sans fenêtre WhatsApp ouverte, le
 * composeur propose les modèles.
 *
 * Toujours visible quand un terme est saisi, même vide : « aucun autre client »
 * est une réponse, pas une absence de section.
 */
export default function AutresClients({ facilityId, terme, dejaAffiches, estGroupe, onOuvrir }: AutresClientsProps) {
    const { data, isLoading, isError, refetch } = useAutresClients(facilityId, terme);
    const clients = (data?.data ?? []).filter((c) => !dejaAffiches.has(c.id));
    const echec = echecSansDonnee({ isError, data });

    return (
        <section aria-label={t('messagerie.autresClients.titre')} className="border-t-8 border-border-color-light">
            <Text variante="sousTitre" as="h2" className="px-4 pb-1 pt-4">
                {t('messagerie.autresClients.titre')}
            </Text>

            {isLoading && (
                <Text variante="petit" ton="attenue" className="px-4 pb-4">
                    {t('messagerie.autresClients.recherche')}
                </Text>
            )}

            {echec && <EtatErreur texte={t('messagerie.autresClients.erreur')} onReessayer={() => void refetch()} />}

            {!isLoading && !echec && clients.length === 0 && (
                <Text variante="petit" ton="attenue" className="px-4 pb-4">
                    {t('messagerie.autresClients.aucun')}
                </Text>
            )}

            <ul>
                {clients.map((client) => (
                    <li key={client.id} className="border-b border-border-color-light">
                        {/* mobile-check-ignore — la ligne EST le contrôle, comme LigneConversation. */}
                        <button
                            type="button"
                            onClick={() => onOuvrir(client.id)}
                            className="flex w-full items-center gap-3 px-4 py-3 text-left active:bg-main-color-light"
                        >
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-main-color-light text-base font-semibold text-main-color-dark">
                                {initiales(client)}
                            </span>
                            <span className="min-w-0 flex-1">
                                <Text as="span" variante="corps" className="block truncate">
                                    {nomComplet(client)}
                                </Text>
                                {estGroupe && client.facility_name && (
                                    <Text as="span" variante="petit" ton="marque" className="block truncate">
                                        {client.facility_name}
                                    </Text>
                                )}
                                <Text as="span" variante="petit" ton="attenue" className="block truncate">
                                    {[client.phone, client.email].filter(Boolean).join(' · ') || '—'}
                                </Text>
                            </span>
                        </button>
                    </li>
                ))}
            </ul>
        </section>
    );
}
